import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getApiErrorMessage, getApiFieldErrors } from "../../lib/apiError";
import { useModeratorsAuthForgotPasswordCreate } from "../../services/moderators/generated/moderators-auth/moderators-auth";
import { EMAIL_PATTERN, RESET_CODE_TTL_MINUTES } from "./ForgetPasswordPage";
import "./AuthPage.css";

/**
 * Moderator reset, step 1: ask for the moderator's email and have the backend
 * email a 6-digit code (`POST /api/v1/moderators/auth/forgotPassword/`).
 * The backend answers identically whether or not the email is a moderator.
 */
const ModeratorForgotPasswordPage = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<{ email?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const forgotPasswordMutation = useModeratorsAuthForgotPasswordCreate({
    mutation: {
      onSuccess: (_data, variables) => {
        toast.success(t("moderatorForgotPassword.toastSent", { email: variables.data.email }));
        navigate("/moderator/reset-password", { state: { email: variables.data.email } });
      },
      onError: (error) => {
        const fieldErrors = getApiFieldErrors(error);
        if (fieldErrors.email) {
          setErrors({ email: fieldErrors.email });
          return;
        }
        setSubmitError(getApiErrorMessage(error, t("forgotPassword.errors.failed")));
      },
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);

    const trimmed = email.trim();
    if (!trimmed) {
      setErrors({ email: t("shared.errors.enterEmail") });
      return;
    }
    if (!EMAIL_PATTERN.test(trimmed)) {
      setErrors({ email: t("shared.errors.validEmail") });
      return;
    }
    setErrors({});

    forgotPasswordMutation.mutate({ data: { email: trimmed } });
  };

  return (
    <div className="auth-page">
      <div className="auth-page__background" />

      <div className="container">
        <div className="auth-page__inner">
          <button type="button" className="auth-page__back-link" onClick={() => navigate("/moderator/login")}>
            {t("shared.backToLogin")}
          </button>

          <section className="auth-card" aria-label={t("moderatorForgotPassword.ariaLabel")}>
            <div className="auth-card__icon-wrapper">
              <div className="auth-card__icon auth-card__icon--primary">
                <span className="auth-card__icon-glyph" aria-hidden="true">
                  <i className="fa-solid fa-user-shield" />
                </span>
              </div>
            </div>

            <header className="auth-card__header">
              <h1 className="auth-card__title">{t("moderatorForgotPassword.title")}</h1>
              <p className="auth-card__subtitle">
                {t("moderatorForgotPassword.subtitle", { minutes: RESET_CODE_TTL_MINUTES })}
              </p>
            </header>

            <form className="auth-form" onSubmit={handleSubmit} noValidate>
              <div className="auth-form__field">
                <label className="auth-form__label" htmlFor="moderator-forgot-email">
                  {t("shared.emailLabel")}
                </label>
                <div className={`auth-form__input-wrapper ${errors.email ? "auth-form__input-wrapper--error" : ""}`}>
                  <span className="auth-form__input-icon" aria-hidden="true">
                    <i className="fa-regular fa-envelope" />
                  </span>
                  <input
                    id="moderator-forgot-email"
                    type="email"
                    className="auth-form__input"
                    placeholder={t("shared.moderatorEmailPlaceholder")}
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </div>
                {errors.email ? <p className="auth-form__error">{errors.email}</p> : null}
              </div>

              {submitError && (
                <p className="auth-form__error auth-form__error--block" role="alert">
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                className="auth-form__submit auth-form__submit--primary"
                disabled={forgotPasswordMutation.isPending}
              >
                {forgotPasswordMutation.isPending ? t("forgotPassword.sendingCode") : t("forgotPassword.sendCode")}
              </button>

              <p className="auth-form__footer-text" style={{ marginTop: "1.5rem" }}>
                {t("forgotPassword.haveCode")}{" "}
                <button
                  type="button"
                  className="auth-form__link"
                  onClick={() => navigate("/moderator/reset-password", { state: { email: email.trim() } })}
                >
                  {t("forgotPassword.enterIt")}
                </button>
              </p>

              <p className="auth-form__footer-text">
                {t("shared.rememberPassword")}{" "}
                <button type="button" className="auth-form__link" onClick={() => navigate("/moderator/login")}>
                  {t("shared.signInLink")}
                </button>
              </p>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ModeratorForgotPasswordPage;
